import { addMinutes, format } from "date-fns";
import { sv } from "date-fns/locale";
import { type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import type { Tables } from "@/integrations/supabase/types";
// Path B+ (Lesson 38/42): bytt inline-anrop till calculatePrivateBookingPerSeatPrice
// mot canonical resolveBookingTargetPrice (kontext-agnostisk pure helper).
// Beteendet är identiskt — single source of truth-disciplin förbättrad.
import {
  resolveBookingTargetPrice,
  type PricingRule,
  type SpecialPriceDate,
} from "@/lib/pricing";
import { type PlayerPriceResult } from "@/lib/playerPricing";
import { getMaxPlayersByCourtType } from "@/lib/courtUtils";
import { calculateDeadline, ensureMinDeadline } from "@/lib/paymentDeadline";
import { awardCoins } from "@/lib/coinService";
import { chargePendingPayment } from "@/lib/chargePendingPayment";
import i18n from "@/i18n";
// Sprint B1b: customer-triggered emails går via send-customer-email edge fn
// (server-side ownership-validation, server-renderad subject/body).
// sendTemplateEmail används endast av admin-flows.

interface BookingAddonInput {
  id: string;
  quantity: number;
  price: number;
}

interface BookingSettings {
  gap_protection?: boolean;
  min_gap_minutes?: number;
}

// T1/G-001: this path is PRIVATE-booking-only. Open match creates a real
// activity via create_activity_v2 inside BookingWizard (same facit-pinned
// builder as the activity wizard) — the old isActivity flag, the creator-share
// charge and the never-populated activity? direct-INSERT branch are removed.
export interface HandleBookOpts {
  durationMinutes: number;
  maxPlayers: number;
  invitedPlayers: string[];
  splitStrategy: "full" | "per_person";
  paymentMethod: string;
  addons?: BookingAddonInput[];
  playerPrices: PlayerPriceResult[];
  discountAmount?: number;
  discountCodeId?: string | null;
  // Bug B fix: booker's resolved category slug (from getPlayerCategory in Book.tsx).
  bookerCategorySlug?: string;
}

export interface HandleBookParams {
  supabase: SupabaseClient<Database>;
  user: { id: string; email?: string; user_metadata?: Record<string, unknown> };
  confirmSlot: { courtId: string; startTime: Date };
  selectedSlots: Array<{ courtId: string; startTime: Date }>;
  courts: Array<Tables<"courts">>;
  organization: {
    id: string;
    name: string;
    settings?: Record<string, unknown> | null;
    max_concurrent_bookings?: number;
    member_max_concurrent_bookings?: number;
  };
  hallId: string;
  hallHours: { start: number; end: number };
  bookings: Array<Tables<"bookings">>;
  pricingRules: PricingRule[];
  specialDates: SpecialPriceDate[];
  billingConfig: Record<string, unknown> | null;
  activeOffer: Tables<"waitlist"> | null;
  isBanned: boolean;
  banUntil: string | null;
  baseUrl: string;
  isMember?: boolean;
}

export interface HandleBookResult {
  success: boolean;
  /** When a Stripe redirect is needed, this URL is returned */
  redirectUrl?: string;
  /** Toast messages the caller should display */
  toasts: Array<{ type: "success" | "error" | "info"; message: string | { jsx: true; pinCode: string } }>;
  /** Whether to clear the selected slot after completion */
  shouldClearSlot: boolean;
  /** Whether to refetch bookings */
  shouldRefetchBookings: boolean;
  /** Whether to invalidate waitlist queries */
  shouldInvalidateWaitlist: boolean;
  /** The ID of the first created booking (for post-booking flows like open match) */
  bookingId?: string;
}

export async function handleBookConfirmation(
  params: HandleBookParams,
  opts: HandleBookOpts,
): Promise<HandleBookResult> {
  const {
    supabase: sb,
    user,
    confirmSlot,
    selectedSlots,
    courts,
    organization,
    hallId,
    hallHours,
    bookings,
    pricingRules,
    specialDates,
    billingConfig,
    activeOffer,
    isBanned,
    banUntil,
    baseUrl,
  } = params;

  const result: HandleBookResult = {
    success: false,
    toasts: [],
    shouldClearSlot: false,
    shouldRefetchBookings: false,
    shouldInvalidateWaitlist: false,
  };

  // Check booking ban
  if (isBanned) {
    result.toasts.push({
      type: "error",
      message: `Du är tillfälligt spärrad från bokning till ${new Date(banUntil!).toLocaleDateString("sv-SE")} pga upprepade uteblivna besök.`,
    });
    return result;
  }

  // G-002: confirmEndTime is the PRIMARY slot's end — used only for the
  // confirmSlot-scoped gap-protection check below. Per-slot end times are
  // computed inside the availability loop and the insert map (each slot's
  // own start + duration); the old shared `endTime` gave every multi-select
  // row the FIRST slot's end → inverted intervals for slots 2..N.
  const confirmEndTime = addMinutes(confirmSlot.startTime, opts.durationMinutes);

  // Concurrent booking limit check
  const maxConcurrent = params.isMember
    ? (organization.member_max_concurrent_bookings ?? 10)
    : (organization.max_concurrent_bookings ?? 3);

  const { count: activeBookingCount } = await sb
    .from("bookings")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user.id)
    .eq("status", "confirmed")
    .gt("start_time", new Date().toISOString());

  if ((activeBookingCount ?? 0) >= maxConcurrent) {
    result.toasts.push({
      type: "error",
      message: `Du har redan ${activeBookingCount} aktiva bokningar (max ${maxConcurrent}).`,
    });
    return result;
  }

  // Gap protection validation
  const bookingSettings = ((organization.settings?.booking ?? {}) as BookingSettings);
  if (bookingSettings.gap_protection) {
    const { validateGapProtection } = await import("@/lib/gapProtection");
    const openTime = `${String(hallHours.start).padStart(2, "0")}:00`;
    const closeTime = `${String(hallHours.end).padStart(2, "0")}:00`;
    const gapError = validateGapProtection(
      confirmSlot.courtId,
      confirmSlot.startTime,
      confirmEndTime,
      bookings,
      openTime,
      closeTime,
      bookingSettings,
    );
    if (gapError) {
      result.toasts.push({ type: "error", message: gapError });
      return result;
    }
  }

  // PR-AJ Layer B — court-availability check via shared helper.
  // Replaces the previous status='confirmed'-only filter that allowed
  // bookings to overlap admin-created blocks. Now checks:
  //   - bookings of status confirmed/blocked (not soft-deleted)
  //   - active recurring_blocks pattern overlap
  // DB trigger trg_bookings_no_overlap (Layer A) is the structural guarantee;
  // this helper is purely UX so users see a clean Swedish toast.
  const { validateCourtAvailability } = await import("@/lib/validateCourtAvailability");
  const { calculateBookingPrice } = await import("@/lib/pricing");
  for (const slot of selectedSlots) {
    // G-002: validate each slot's OWN window (start + duration), not the
    // first slot's end — the shared end validated the wrong window for
    // slots 2..N in multi-select.
    const slotEnd = addMinutes(slot.startTime, opts.durationMinutes);
    const availability = await validateCourtAvailability(sb, {
      courtId: slot.courtId,
      startTime: slot.startTime,
      endTime: slotEnd,
    });
    if (!availability.available) {
      const courtObj = courts.find((c) => c.id === slot.courtId);
      result.toasts.push({
        type: "error",
        message: `${courtObj?.name ?? "Banan"}: ${availability.message}`,
      });
      return result;
    }
  }

  // Multi-court: generate group id if >1 court
  const bookingGroupId = selectedSlots.length > 1 ? crypto.randomUUID() : null;

  // Insert all bookings
  // ─── Sprint R1: reserved → confirmed enforcement ──────────────────────
  // The booking starts in `status='reserved'` for any paid-price online
  // flow that requires settled payment before slot-blocking. The booking
  // is then promoted to `status='confirmed'` at the appropriate settle
  // point:
  //   - saved-card MIT off-session success → inline UPDATE below
  //   - Stripe Checkout webhook (payment_intent.succeeded /
  //     checkout.session.completed) → via the helper-gated promotion
  //     in supabase/functions/_shared/stripe-webhook-handler.ts
  //   - credits/klippkort deduction success → inline UPDATE below
  // The trigger `check_booking_no_overlap` Section 1 and the 3 RPC
  // pre-flights treat live `reserved` (status='reserved' AND
  // payment_expires_at > now()) as occupying; expired reserved is free.
  //
  // Dual-expiry window per the operator decision (D-R1-3 follow-up):
  //   - 60s for instant paths (saved-card MIT, credits): charge or
  //     deduction is synchronous; if it does not return success within
  //     ~60s the slot must be released for other bookers.
  //   - 5min for Stripe Checkout redirect: new customer needs time to
  //     enter card details and complete 3DS on Stripe's hosted page.
  //
  // `effectivePrice === 0` (full discount or admin-marked free) means
  // there is no payment to wait for — those bookings INSERT directly as
  // confirmed+paid (handled by `bookingStatus` default to undefined,
  // which lets the column-default 'confirmed' apply).
  const RESERVED_EXPIRY_INSTANT_MS = 60 * 1000;         // saved-card MIT, credits
  const RESERVED_EXPIRY_CHECKOUT_MS = 5 * 60 * 1000;    // Stripe Checkout redirect

  // Bug B fix #3: PER-SEAT pricing for PRIVATE bookings (operator-locked).
  // See calculatePrivateBookingPerSeatPrice in src/lib/pricing.ts for the
  // formula. Mirrors the open-match per-participant share model (proven in
  // open-match-share-zero-discovery.md). Both UI (BookingWizard.pricePerCourt)
  // and this INSERT path use the same helper so they always agree (F4).
  //
  // Standard-booker no-op (F5): when booker AND all invited are 'standard',
  // the all-standard guard returns courtPriceMinorForCategory('standard')
  // EXACTLY — same value as pre-Bug-B for standard users.
  //
  // (T1/G-001: the open-match variant of this path is gone — open match is
  // created by create_activity_v2. 'standard' stays the reference rate for
  // the per-seat empty-seat calculation in private bookings.)
  //
  // Invited player categories: derived from opts.playerPrices (PlayerPriceResult
  // includes categorySlug per player). playerPrices[0] is the booker, [1..N]
  // are invited (in the order they were added). Falls back to all-'standard'
  // if playerPrices not populated (defensive — the wizard's `pricesReady`
  // gate prevents confirm before playerPrices loads).
  const invitedCategorySlugs: string[] = opts.playerPrices.length > 0
    ? opts.playerPrices.slice(1).map((p) => p.categorySlug ?? "standard")
    : opts.invitedPlayers.map(() => "standard");

  const bookingInserts = selectedSlots.map((slot) => {
    // Standard court price for this slot — used for:
    //  (a) open-match base_court_price_minor reference
    //  (b) per-hour rate display (slotPrice.perHour) — keeps the listed
    //      court rate consistent regardless of booker category
    //  (c) per-seat formula's empty-seat rate
    const stdSlotPrice = calculateBookingPrice(
      pricingRules, hallId, slot.startTime, opts.durationMinutes, "standard", specialDates,
    );

    // Compute slot total in MINOR (öre, exact) via canonical resolver.
    // Path B+ (Lesson 38): EN call-site för båda open-match + private —
    // resolveBookingTargetPrice branch:ar internt och returnerar öre-exakt
    // total. Tidigare hade vi separata grenar med duplicerad math; nu är
    // det single source of truth som matchar move-flow (MoveBookingDialog),
    // admin-direct-flow (AdminScheduleGrid) och reschedule-flow (RescheduleDialog).
    const court = courts.find((c) => c.id === slot.courtId);
    const seatCount = court?.max_players
      ?? getMaxPlayersByCourtType((court?.court_type as Parameters<typeof getMaxPlayersByCourtType>[0]) ?? undefined);
    const target = resolveBookingTargetPrice({
      // T1/G-001: always private here — open match no longer flows through
      // this path (it creates via create_activity_v2 in BookingWizard).
      bookingKind: "private",
      targetSlot: {
        hallId,
        startTime: slot.startTime,
        durationMinutes: opts.durationMinutes,
        seatCount,
        pricingRules,
        specialDates,
      },
      bookerCategorySlug: opts.bookerCategorySlug ?? "standard",
      invitedCategorySlugs,
    });
    const slotTotalMinor = target.totalMinor;
    // Legacy price_total column (integer kronor) — rounded from minor.
    // Canonical öre value goes to price_total_minor below (explicit write
    // bypasses dual_write_bookings_minor's price_total × 100 fallback,
    // preserving fractional kr like 367.50 → 36750 öre without drift).
    const slotPriceTotal = Math.round(slotTotalMinor / 100);

    // Determine initial payment_status, booking status, and payment_expires_at
    // based on payment method + effective price.
    let paymentStatus: string;
    let bookingStatus: "reserved" | undefined;   // undefined → DB default 'confirmed'
    let paymentExpiresAt: string | null = null;
    // Use öre for effectivePrice comparison to avoid kr-rounding hiding a
    // non-zero balance (e.g. perSeatMinor=36750 → slotPriceTotal=368, discount=368
    // would give effectivePrice=0 in kr but should be 36750-36800=-50 → clamped 0;
    // safer to compute in öre and clamp).
    const discountMinor = Math.round((opts.discountAmount ?? 0) * 100);
    const effectivePriceMinor = Math.max(0, slotTotalMinor - discountMinor);
    const effectivePrice = effectivePriceMinor / 100;

    if (effectivePrice === 0) {
      // Fully covered by discount — no payment to wait for. Confirm immediately.
      paymentStatus = "paid";
      bookingStatus = undefined;
    } else if (opts.paymentMethod === "stripe") {
      // Stripe Checkout redirect — 5min reserved window for customer to enter card.
      // Promoted to 'confirmed' by the stripe-webhook-handler when the helper
      // booking_booker_obligation_settled() returns TRUE.
      paymentStatus = "pending_payment";
      bookingStatus = "reserved";
      paymentExpiresAt = new Date(Date.now() + RESERVED_EXPIRY_CHECKOUT_MS).toISOString();
    } else if (opts.paymentMethod === "invoice") {
      // Invoice path stays as legacy pending_invoice + status defaulted to
      // confirmed (admin-organised back-office payment). Out of R1 scope.
      paymentStatus = "pending_invoice";
      bookingStatus = undefined;
    } else if (opts.paymentMethod === "credits") {
      // Klippkort/credits deduction is synchronous below. Use the short
      // 60s reserved window; the inline UPDATE on successful deduction
      // promotes to 'confirmed' + payment_status='paid'.
      paymentStatus = "pending_payment";
      bookingStatus = "reserved";
      paymentExpiresAt = new Date(Date.now() + RESERVED_EXPIRY_INSTANT_MS).toISOString();
    } else if (opts.paymentMethod?.startsWith("saved_card:")) {
      // Saved-card MIT off-session — synchronous below. 60s reserved window.
      paymentStatus = "pending_payment";
      bookingStatus = "reserved";
      paymentExpiresAt = new Date(Date.now() + RESERVED_EXPIRY_INSTANT_MS).toISOString();
    } else {
      // Unknown/legacy path — reserve to be safe.
      paymentStatus = "pending_payment";
      bookingStatus = "reserved";
      paymentExpiresAt = new Date(Date.now() + RESERVED_EXPIRY_INSTANT_MS).toISOString();
    }

    // price_per_hour: keep the STANDARD court rate as the displayed hourly
    // rate (the "court list rate" reference is unchanged regardless of who
    // books). price_per_hour_minor mirrors via dual_write trigger.
    const pricePerHour = stdSlotPrice.perHour;
    const pricePerHourMinor =
      pricePerHour != null ? Math.round(pricePerHour * 100) : null;

    return {
      court_id: slot.courtId,
      user_id: user.id,
      // PR-AN: player books for self → created_by = user_id. Same value, but
      // populated explicitly so render layer can read created_by uniformly.
      created_by: user.id,
      start_time: slot.startTime.toISOString(),
      // G-002: per-row end_time = this slot's own start + duration. The old
      // shared endTime (first slot's end) produced inverted intervals
      // (end < start) for later slots — which the overlap trigger silently
      // accepts for status='reserved' (degenerate tstzrange).
      end_time: addMinutes(slot.startTime, opts.durationMinutes).toISOString(),
      duration_minutes: opts.durationMinutes,
      // T1/G-001: private-only path — open-match bookings are created by
      // create_activity_v2 (which stamps is_open_match itself).
      is_open_match: false,
      max_players: opts.maxPlayers,
      price_per_hour: pricePerHour,
      price_total: slotPriceTotal,
      // Bug B fix #3: explicit price_total_minor preserves öre precision
      // (e.g. 367.50 kr → 36750 öre) — bypasses dual_write_bookings_minor
      // trigger's price_total*100 fallback which would drift to 36800.
      price_total_minor: slotTotalMinor,
      ...(pricePerHourMinor != null ? { price_per_hour_minor: pricePerHourMinor } : {}),
      booking_group_id: bookingGroupId,
      payment_status: paymentStatus,
      ...(bookingStatus ? { status: bookingStatus } : {}),
      ...(paymentExpiresAt ? { payment_expires_at: paymentExpiresAt } : {}),
    };
  });

  const { data: createdBookings, error } = await sb
    .from("bookings")
    .insert(bookingInserts as Tables<"bookings">[])
    .select();

  if (error) {
    result.toasts.push({ type: "error", message: "Kunde inte boka: " + error.message });
    return result;
  }

  // Use first booking as primary reference
  const data = createdBookings?.[0];
  if (!data) {
    result.toasts.push({ type: "error", message: "Bokning skapades men kunde inte hämtas" });
    return result;
  }

  // Total price across all courts (subtract discount)
  const grossTotal = bookingInserts.reduce((sum, b) => sum + (b.price_total ?? 0), 0);
  const discount = opts.discountAmount ?? 0;

  // T1/G-001: private-only — the old open-match creator-share charge
  // (creatorShare/creatorFinal + 3 SEK Stripe minimum) is removed; per
  // decision 9 the organizer pays at JOIN via join_activity_v2 instead.
  const priceInfo = {
    total: Math.max(0, grossTotal - discount),
    perHour: bookingInserts[0].price_per_hour,
  };

  // Track voucher usage if a discount code was applied
  if (discount > 0 && opts.discountCodeId) {
    const { data: voucher } = await sb
      .from("vouchers" as any)
      .select("uses_count")
      .eq("id", opts.discountCodeId)
      .maybeSingle();
    if (voucher) {
      await sb
        .from("vouchers" as any)
        .update({ uses_count: ((voucher as any).uses_count ?? 0) + 1 })
        .eq("id", opts.discountCodeId);
    }
  }

  // If booked from waitlist offer, mark as accepted
  if (activeOffer && activeOffer.court_id === confirmSlot.courtId) {
    await sb.from("waitlist").update({
      status: "accepted",
      accepted_at: new Date().toISOString(),
    }).eq("id", activeOffer.id);
    result.shouldInvalidateWaitlist = true;
  }

  const orgId = organization.id;

  if (opts.invitedPlayers.length > 0 && createdBookings) {
    const playerInserts = createdBookings.flatMap((booking: Tables<"bookings">) =>
      opts.invitedPlayers.map((pid) => ({
        booking_id: booking.id,
        user_id: pid,
        payment_status: "pending" as const,
      })),
    );
    await sb.from("booking_players").insert(playerInserts);
  }

  // Save per-player pricing data (on primary booking)
  if (opts.playerPrices && opts.playerPrices.length > 0) {
    for (const pp of opts.playerPrices) {
      await sb
        .from("booking_players")
        .update({
          category_slug: pp.categorySlug,
          price_share: pp.totalShare,
          discounted_minutes: pp.discountedMinutes,
          standard_minutes: pp.standardMinutes,
        })
        .eq("booking_id", data.id)
        .eq("user_id", pp.userId);
    }
  }

  // Helper to find court name
  const selectedCourt = courts.find((c) => c.id === confirmSlot.courtId);

  // Email helper — only call when payment is confirmed (status = 'paid').
  // Sprint B1b: server-side dispatcher (send-customer-email) validerar
  // ownership och renderar template från verifierad booking-data. Multi-slot
  // bookings aggregeras via booking_group_id på server-sidan.
  const fireConfirmationEmail = () => {
    if (!data?.id) return;
    sb.functions
      .invoke("send-customer-email", {
        body: { template: "booking_confirmation", bookingId: data.id },
      })
      .then(({ error }) => {
        if (error) console.error("[BOOKING EMAIL ERROR]", error);
      });
  };

  // Handle fully discounted bookings (0 kr) — mark as paid immediately. Sprint D dual-write.
  if (orgId && priceInfo.total === 0 && discount > 0) {
    await sb.from("booking_payments").insert({
      organization_id: orgId,
      booking_id: data.id,
      user_id: user.id,
      amount: 0,
      amount_minor: 0,
      currency: "SEK",
      payment_type: "full" as const,
      status: "paid" as const,
      payment_method: "discount",
      paid_at: new Date().toISOString(),
    } as Record<string, unknown>);
    result.toasts.push({ type: "success", message: "Bokning bekräftad! Rabatten täckte hela beloppet." });
    fireConfirmationEmail();
  }

  // Create booking_payment records and handle payment
  if (orgId && priceInfo.total !== null && priceInfo.total > 0) {
    const playerCount = 1 + opts.invitedPlayers.length;

    if (opts.paymentMethod === "credits") {
      // Pay with credits — Sprint D dual-state read + dual-write.
      const { data: creditRow } = await sb
        .from("player_credits" as any)
        .select("balance, balance_minor, currency")
        .eq("user_id", user.id)
        .eq("organization_id", orgId)
        .maybeSingle();

      const creditMinor = (() => {
        const m = (creditRow as { balance_minor?: number | null } | null)?.balance_minor;
        const l = (creditRow as { balance?: number } | null)?.balance;
        if (typeof m === "number" && Number.isFinite(m)) return m;
        if (typeof l === "number" && Number.isFinite(l)) return Math.round(l * 100);
        return 0;
      })();
      const creditBalance = Math.round(creditMinor / 100);
      if (creditBalance < priceInfo.total) {
        result.toasts.push({ type: "error", message: "Otillräckligt klippkortssaldo" });
        return result;
      }

      // Deduct credits — dual-write legacy + _minor.
      const priceMinor = Math.round(priceInfo.total * 100);
      const newBalanceMinor = creditMinor - priceMinor;
      const newBalanceKronor = Math.round(newBalanceMinor / 100);
      await sb
        .from("player_credits" as any)
        .update({
          balance: newBalanceKronor,
          balance_minor: newBalanceMinor,
          currency: "SEK",
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .eq("organization_id", orgId);

      // Log credit transaction — dual-write.
      await sb.from("credit_transactions" as any).insert({
        user_id: user.id,
        organization_id: orgId,
        amount: -priceInfo.total,                // legacy kronor
        amount_minor: -priceMinor,               // öre
        currency: "SEK",
        type: "booking_payment",
        reference_id: data.id,
        description: `Bokning: ${selectedCourt?.name ?? "Bana"}`,
      });

      // Create paid payment record — Sprint D dual-write
      await sb.from("booking_payments").insert({
        organization_id: orgId,
        booking_id: data.id,
        user_id: user.id,
        amount: priceInfo.total,                              // legacy kronor
        amount_minor: Math.round(priceInfo.total * 100),      // öre
        currency: "SEK",
        payment_type: "full" as const,
        status: "paid" as const,
        payment_method: "credits",
        paid_at: new Date().toISOString(),
      });
      // R1 promotion gate: reserved → confirmed iff the booker's
      // obligation is settled. Credits deduction succeeded + the
      // booking_payments row above is 'paid' → helper sees it +
      // returns TRUE for no-split (and for split organizer-share
      // since we paid the full share).
      const { data: settledRow } = await sb.rpc("booking_booker_obligation_settled" as any, {
        _booking_id: data.id,
      });
      const isSettled = settledRow === true;
      await sb.from("bookings").update({
        ...(isSettled ? { status: "confirmed" as any } : {}),
        payment_status: "paid",
        payment_expires_at: null,
      } as any).eq("id", data.id);
      fireConfirmationEmail();
    } else if (opts.splitStrategy === "per_person" && playerCount > 1 && opts.paymentMethod === "stripe") {
      const splitAmount = Math.ceil(priceInfo.total / opts.maxPlayers);
      const splitAmountMinor = Math.round(splitAmount * 100);
      const allPlayers = [user.id, ...opts.invitedPlayers];
      const payDeadline = ensureMinDeadline(
        calculateDeadline(confirmSlot.startTime, new Date(), billingConfig ?? {}),
      );

      // Sprint D dual-write: legacy + _minor + currency.
      const { data: paymentRows } = await sb.from("booking_payments").insert(
        allPlayers.map((pid) => ({
          organization_id: orgId,
          booking_id: data.id,
          user_id: pid,
          amount: splitAmount,                  // legacy kronor
          amount_minor: splitAmountMinor,       // öre
          currency: "SEK",
          payment_type: "split" as const,
          status: "pending" as const,
          deadline: payDeadline?.toISOString() ?? null,
          guarantor_user_id: user.id, // booking creator is guarantor
        })),
      ).select("id, user_id");

      const courtName = selectedCourt?.name ?? "Bana";
      const dateStr = format(confirmSlot.startTime, "EEEE d MMM HH:mm", { locale: sv });
      await sb.from("notifications").insert(
        opts.invitedPlayers.map((pid) => ({
          user_id: pid,
          organization_id: orgId,  // PR-B: org-scope per Phase 1 RLS
          type: "payment_invite",
          title: "Du är inbjuden till en bokning",
          body: `${courtName}, ${dateStr}. Din andel: ${splitAmount} kr. Betala via Mina bokningar.`,
          data: { booking_id: data.id, amount: splitAmount },
        })),
      );

      const bookerPayment = paymentRows?.find((p: { id: string; user_id: string }) => p.user_id === user.id);
      if (bookerPayment) {
        try {
          const { data: checkoutData, error: checkoutError } = await sb.functions.invoke(
            "create-checkout-session",
            {
              body: {
                booking_id: data.id,
                amount: splitAmount,
                payment_id: bookerPayment.id,
                success_url: `${baseUrl}/bookings?payment=success`,
                cancel_url: `${baseUrl}/bookings`,
              },
            },
          );
          if (checkoutError) {
            // D1: 403 {skipped:"charge_disabled"} (booking_charge_enabled OFF) is NOT a failure.
            // supabase-js surfaces non-2xx as FunctionsHttpError with the Response on .context.
            const ctx = (checkoutError as { context?: Response }).context;
            if (ctx && typeof ctx.json === "function") {
              try {
                const sbody = (await ctx.json()) as { skipped?: string } | null;
                if (sbody?.skipped === "charge_disabled") {
                  await sb.from("bookings").update({
                    status: "cancelled" as any,
                    payment_status: "failed",
                    payment_expires_at: null,
                  } as any).eq("id", data.id);
                  result.success = false;
                  result.toasts.push({ type: "info", message: i18n.t("booking:toast.bookingChargeUnavailable") });
                  return result;
                }
              } catch {
                // non-JSON error body — fall through to the failure path
              }
            }
            throw checkoutError;
          }
          if (checkoutData?.url) {
            result.success = true;
            result.redirectUrl = checkoutData.url;
            return result;
          }
          // R1 fix: Checkout invocation returned no URL (and no error).
          // Treat as failure — do NOT fall through to a "booked" UI.
          throw new Error("Stripe Checkout: ingen redirect-URL returnerad");
        } catch (err: unknown) {
          // R1 fix: silent catch removed. Cancel the reserved row so
          // the slot frees immediately (no 5min wait for cron) and the
          // user can retry. Bubble failure up so the wizard stays on
          // the payment step instead of advancing to "booked".
          console.error("Stripe checkout error (split-share path):", err);
          await sb.from("bookings").update({
            status: "cancelled" as any,
            payment_status: "failed",
            payment_expires_at: null,
          } as any).eq("id", data.id);
          result.success = false;
          result.toasts.push({
            type: "error",
            message: "Kunde inte starta kortbetalning. Bokningen avbröts — försök igen.",
          });
          return result;
        }
      }
    } else if (opts.paymentMethod.startsWith("saved_card:")) {
      // Saved card — charge immediately via off-session MIT
      const savedCardId = opts.paymentMethod.replace("saved_card:", "");

      // T1b/F3: a multi-court checkout creates N rows (same booking_group_id)
      // but the payment row lives on the PRIMARY row only. Every state
      // transition below must therefore hit ALL created rows — the old
      // .eq(data.id) promoted/cancelled only the primary, leaving siblings in
      // pending_payment until the release cron dropped them (customer paid
      // for 2 courts, kept 1).
      const createdIds = (createdBookings ?? []).map((b) => b.id);

      // Set pending payment status with short expiry (charge happens immediately)
      await sb.from("bookings").update({
        payment_status: "pending_payment",
        payment_expires_at: new Date(Date.now() + 60_000).toISOString(),
      } as any).in("id", createdIds);

      // Sprint D dual-write
      const { data: paymentRow } = await sb.from("booking_payments").insert({
        organization_id: orgId,
        booking_id: data.id,
        user_id: user.id,
        amount: priceInfo.total,                              // legacy kronor
        amount_minor: Math.round(priceInfo.total * 100),      // öre
        currency: "SEK",
        payment_type: "full" as const,
        status: "pending" as const,
        payment_method: "card",
      } as Record<string, unknown>).select("id").single();

      // 2d-ii Step 1 (2A-i): the off-session charge + paid-update moved verbatim into
      // chargePendingPayment (shared with the activity-join path). Booking-specific reactions
      // (promote on success / cancel on failure) stay here, behaviorally unchanged.
      const charge = await chargePendingPayment({
        userId: user.id,
        orgId,
        amountMinor: priceInfo.total * 100,
        paymentId: paymentRow?.id ?? "",
        savedCardId,
        description: `Bokning ${confirmSlot.courtId}`,
        relatedEntityType: "booking",
        relatedEntityId: data.id,
        sb,
      });
      if (charge.status === "skipped") {
        // D1: booking_charge_enabled OFF for this org → charge-saved-card skipped BEFORE Stripe.
        // NOT a decline: release the reserved rows (frees the slots) + a NEUTRAL info toast — never
        // "försök igen" (retrying can't help; online payment is unavailable for this club).
        await sb.from("bookings").update({
          status: "cancelled" as any,
          payment_status: "failed",
          payment_expires_at: null,
        } as any).in("id", createdIds);
        result.success = false;
        result.toasts.push({ type: "info", message: i18n.t("booking:toast.bookingChargeUnavailable") });
        return result;
      }
      if (charge.status !== "paid") {
        if (charge.network) console.error("[saved-card] network error:", charge.error);
        else console.error("[saved-card] charge failed:", charge.error);
        await sb.from("bookings").update({
          status: "cancelled" as any,
          payment_status: "failed",
        } as any).in("id", createdIds);
        result.success = false;
        result.toasts.push({
          type: "error",
          message: charge.network ? "Betalningen misslyckades. Försök igen." : (charge.error || "Betalningen misslyckades"),
        });
        return result;
      }
      // R1 promotion gate: reserved → confirmed iff the booker's obligation
      // is settled. (booking_payments was marked paid inside chargePendingPayment.)
      // The settled check stays on data.id — that's where the group's single
      // payment row lives; the promotion covers ALL group rows (T1b/F3).
      const { data: settledRow } = await sb.rpc("booking_booker_obligation_settled" as any, {
        _booking_id: data.id,
      });
      const isSettled = settledRow === true;
      await sb.from("bookings").update({
        ...(isSettled ? { status: "confirmed" as any } : {}),
        payment_status: "paid",
        payment_expires_at: null,
      } as any).in("id", createdIds);

      result.success = true;
      result.toasts.push({ type: "success", message: "Bokning bekräftad!" });
    } else if (opts.paymentMethod === "stripe") {
      // Stripe full payment (redirect to Checkout)
      const fullDeadline = ensureMinDeadline(
        calculateDeadline(confirmSlot.startTime, new Date(), billingConfig ?? {}),
      );
      // Sprint D dual-write
      const { data: paymentRow } = await sb.from("booking_payments").insert({
        organization_id: orgId,
        booking_id: data.id,
        user_id: user.id,
        amount: priceInfo.total,                              // legacy kronor
        amount_minor: Math.round(priceInfo.total * 100),      // öre
        currency: "SEK",
        payment_type: "full" as const,
        status: "pending" as const,
        payment_method: "stripe",
        deadline: fullDeadline?.toISOString() ?? null,
      } as Record<string, unknown>).select("id").single();

      if (paymentRow) {
        // 2d-ii Step 1 (2A-i): the create-checkout-session invoke moved verbatim into
        // chargePendingPayment (no savedCardId → Checkout redirect). Cancel-on-failure stays here.
        const charge = await chargePendingPayment({
          userId: user.id,
          orgId,
          amountMinor: Math.round(priceInfo.total * 100),
          paymentId: paymentRow.id,
          description: `Bokning ${confirmSlot.courtId}`,
          relatedEntityType: "booking",
          relatedEntityId: data.id,
          bookingId: data.id,
          successUrl: `${baseUrl}/bookings?payment=success`,
          cancelUrl: `${baseUrl}/bookings`,
          sb,
        });
        if (charge.status === "redirect") {
          result.success = true;
          result.redirectUrl = charge.url;
          return result;
        }
        if (charge.status === "skipped") {
          // D1: booking_charge_enabled OFF → create-checkout-session returned 403 skipped (no session).
          // NOT a failure: release the reserved row + NEUTRAL info toast (never "försök igen").
          await sb.from("bookings").update({
            status: "cancelled" as any,
            payment_status: "failed",
            payment_expires_at: null,
          } as any).eq("id", data.id);
          result.success = false;
          result.toasts.push({ type: "info", message: i18n.t("booking:toast.bookingChargeUnavailable") });
          return result;
        }
        // R1 fix: Checkout failed to start — cancel the reserved row so the slot frees
        // immediately (no 5min cron wait) and the user can retry; bubble failure up.
        console.error("Stripe checkout error (no-saved-card path):", charge.error);
        await sb.from("bookings").update({
          status: "cancelled" as any,
          payment_status: "failed",
          payment_expires_at: null,
        } as any).eq("id", data.id);
        result.success = false;
        result.toasts.push({
          type: "error",
          message: "Kunde inte starta kortbetalning. Bokningen avbröts — försök igen.",
        });
        return result;
      }
    } else {
      // Offline methods: cash, invoice, wellness — Sprint D dual-write
      await sb.from("booking_payments").insert({
        organization_id: orgId,
        booking_id: data.id,
        user_id: user.id,
        amount: priceInfo.total,                              // legacy kronor
        amount_minor: Math.round(priceInfo.total * 100),      // öre
        currency: "SEK",
        payment_type: "full" as const,
        status: "pending" as const,
        payment_method: opts.paymentMethod,
      } as Record<string, unknown>);

      const methodLabels: Record<string, string> = { cash: "kontant", invoice: "faktura", wellness: "friskvård" };
      result.toasts.push({ type: "info", message: `Bokning skapad! Betala ${methodLabels[opts.paymentMethod] ?? opts.paymentMethod} i receptionen.` });
    }
  }

  // Notify invited players (general invite when no split)
  if (opts.invitedPlayers.length > 0 && opts.splitStrategy !== "per_person") {
    const courtName = selectedCourt?.name ?? "Bana";
    const dateStr = format(confirmSlot.startTime, "EEEE d MMM HH:mm", { locale: sv });
    await sb.from("notifications").insert(
      opts.invitedPlayers.map((pid) => ({
        user_id: pid,
        organization_id: orgId,  // PR-B: org-scope per Phase 1 RLS
        type: "booking_invite",
        title: "Du är inbjuden till en bokning",
        body: `${courtName}, ${dateStr}.`,
        data: { booking_id: data.id },
      })),
    );
  }

  // Award coins for booking
  if (orgId) {
    awardCoins(user.id, orgId, "earned_booking", data.id, "Coins för bokning").catch(() => {});
  }

  // T1/G-001: the dormant opts.activity direct-INSERT branch (activities +
  // activity_players + activity_id backfill) is removed — it was proven dead
  // (opts.activity was never populated by any caller) and is REPLACED by the
  // atomic create_activity_v2 RPC call in BookingWizard.handleCreateOpenMatch.

  // Pin code toast
  result.toasts.push({
    type: "success",
    message: { jsx: true, pinCode: data.pin_code },
  });

  result.success = true;
  result.shouldRefetchBookings = true;
  result.shouldClearSlot = true;
  result.bookingId = data.id;
  return result;
}
